import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

export interface ViewportOverlayProps {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  /** Backdrop click closes when true. */
  dismissible?: boolean;
  className?: string;
  scrimClassName?: string;
}

/** Full-viewport layer — above page scroll/sticky UI (`--z-overlay`). */
export function ViewportOverlay({
  open,
  onClose,
  children,
  dismissible = true,
  className,
  scrimClassName,
}: ViewportOverlayProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn('fixed inset-0 z-[var(--z-overlay)] flex items-center justify-center p-4', className)}
      role="presentation"
    >
      {onClose && (
        <div
          className={cn(
            'cal-scrim absolute inset-0 bg-[rgba(36,41,54,0.45)]',
            scrimClassName,
          )}
          onClick={dismissible ? onClose : undefined}
          aria-hidden
        />
      )}
      <div className="relative z-[1] flex max-h-full w-full max-w-full items-center justify-center">{children}</div>
    </div>,
    document.body,
  );
}
